import type { ProviderGetImage } from '@nuxt/image';
import { joinURL } from 'ufo';
import { createOperationsGenerator } from '#image';

const queryParamOperationsGenerator = createOperationsGenerator({
  joinWith: '&',
});

const thumborOperationsGenerator = createOperationsGenerator({
  formatter: (operationKey, operationValue) =>
    `filters:${operationKey}(${operationValue})`,
  joinWith: '/',
});

export const getImage: ProviderGetImage = (
  src,
  { modifiers = {}, baseURL } = {},
) => {
  const config = useRuntimeConfig();
  const storageCloudfrontDistribution =
    (config.public.storageCloudfrontDistribution as string) || '';
  const publicCloudfrontDistribution =
    (config.public.publicCloudfrontDistribution as string) || '';
  const bucketPrefix = (config.public.s3BucketEnvFolderPrefix as string) || '';

  if (!baseURL) {
    baseURL = publicCloudfrontDistribution;
  }

  let formattedSrc = src;

  // Preserve just the path part of the src if it's an absolute URL.
  // If it's an url to the storage s3 distribution, we don't want to use
  // it publically because it can't do the image transformations.
  // If it's an url to the public distribution already, we still remove it
  // so we can make sure to prepend the environment folder further down
  if (formattedSrc.startsWith(storageCloudfrontDistribution)) {
    formattedSrc = formattedSrc.replace(storageCloudfrontDistribution, '');
  }
  if (formattedSrc.startsWith(baseURL)) {
    formattedSrc = formattedSrc.replace(baseURL, '');
  }

  // If the src doesn't start with the s3 bucket environment subpath
  // (/development/... etc), add it
  // example: /images/thumbs/phone.png -> /development/images/thumbs/phone.png
  if (
    !!bucketPrefix &&
    bucketPrefix !== '' &&
    !formattedSrc.startsWith(bucketPrefix) &&
    !formattedSrc.startsWith(`/${bucketPrefix}`)
  ) {
    formattedSrc = `/${bucketPrefix}${formattedSrc}`;
  }

  // https://docs.aws.amazon.com/solutions/latest/dynamic-image-transformation-for-amazon-cloudfront/use-supported-query-param-edits.html
  const modifiersToIncludeForQueryParams = [
    'format',
    'fit',
    'width',
    'height',
    'rotate',
    'flip',
    'flop',
    'greyscale',
  ];

  // https://docs.aws.amazon.com/solutions/latest/dynamic-image-transformation-for-amazon-cloudfront/use-supported-thumbor-filters.html
  const modifiersToIncludeForThumborFilters = [
    'quality',
    'blur',
    'background_color',
    'fill',
    'equalize',
    'rgb',
  ];

  const queryParamModifiers: { [key: string]: string } = {};
  modifiersToIncludeForQueryParams.forEach((key) => {
    if (key in modifiers) {
      queryParamModifiers[key] = modifiers[key];
    }
  });

  const thumborModifiers: { [key: string]: string } = {};
  modifiersToIncludeForThumborFilters.forEach((key) => {
    if (key in modifiers) {
      thumborModifiers[key] = modifiers[key];
    }
  });

  const operations = queryParamOperationsGenerator(queryParamModifiers);
  const thumborOperations = thumborOperationsGenerator(thumborModifiers);

  const thumborOpsUrlSeg = thumborOperations ? `${thumborOperations}` : '';
  const queryParamOpsUrlSeg = operations ? `?${operations}` : '';

  const url = joinURL(
    baseURL,
    thumborOpsUrlSeg + formattedSrc + queryParamOpsUrlSeg,
  );

  return {
    url,
  };
};
